import { IMyRaffleRewardClaimListItemDTO } from "@hooks/dtos/raffles/IRaffleRewardClaimDTO";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import {
  FaArrowUpRightFromSquare,
  FaEye,
  FaTicket,
} from "react-icons/fa6";
import { RewardThumb } from "./RewardThumb";

const masks = new Masks();

interface IProps {
  item: IMyRaffleRewardClaimListItemDTO;
}

function getCampaignUrl(item: IMyRaffleRewardClaimListItemDTO) {
  if (item.raffle.key) {
    return `/campaign/default?key=${item.raffle.key}`;
  }

  return `/campaign/default?campaign=${item.raffle.campaign}`;
}

function getRewardInfoUrl(item: IMyRaffleRewardClaimListItemDTO) {
  const params = new URLSearchParams();
  const firstReward = item.rewards[0];
  if (firstReward?.claimId) params.append("claim", String(firstReward.claimId));

  return `/my-rewards/detail?${params.toString()}`;
}

function CampaignInfo({ item }: IProps) {
  const status = getRaffleStatus(item.raffle.status as RaffleStatusTypes);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-start gap-1 mb-1">
        <span
          className={`badge bg-${status.color} bg-opacity-10 text-dark border border-${status.color} fw-semibold`}
          title={status.title}
          style={{ borderRadius: 8 }}
        >
          {status.text}
        </span>
        <Link
          to={getCampaignUrl(item)}
          className="badge bg-light text-muted border text-decoration-none fw-normal"
          title="Abrir campanha por key."
          style={{ borderRadius: 8 }}
        >
          #{item.raffle.key}
        </Link>
        <Link
          to={getCampaignUrl(item)}
          className="badge bg-light text-muted border text-decoration-none fw-normal"
          title="Abrir campanha pela key vinculada a este slug."
          style={{ borderRadius: 8 }}
        >
          #{item.raffle.campaign}
        </Link>
      </div>
      <Link
        to={getCampaignUrl(item)}
        className="fw-bold text-dark text-decoration-none d-block"
        title="Abrir campanha premiada."
      >
        {item.raffle.name}
      </Link>
    </>
  );
}

function RewardThumbStack({ item }: IProps) {
  return (
    <div className="d-flex align-items-center mb-2" style={{ minHeight: 42 }}>
      {item.rewards.map((reward, index) => (
        <div
          key={reward.claimId}
          title={reward.reward.name}
          style={{
            marginLeft: index === 0 ? 0 : -10,
            position: "relative",
            zIndex: item.rewards.length - index,
          }}
        >
          <RewardThumb
            src={reward.reward.thumb}
            alt={reward.reward.name}
            size={40}
          />
        </div>
      ))}
    </div>
  );
}

function RewardNames({ item }: IProps) {
  return (
    <div className="fw-semibold text-dark" style={{ lineHeight: 1.35 }}>
      {item.rewards.map((reward, index) => (
        <span key={reward.claimId}>
          {index > 0 ? ", " : ""}
          {reward.reward.name}
        </span>
      ))}
    </div>
  );
}

export function RewardCampaignDesktopRow({ item }: IProps) {
  return (
    <tr>
      <td className="text-start" style={{ minWidth: 360 }}>
        <div className="d-flex align-items-center justify-content-start gap-3 text-start">
          <Link to={getCampaignUrl(item)} title="Abrir campanha premiada.">
            <RewardThumb
              src={item.raffle.thumb}
              alt={item.raffle.name}
              size={52}
            />
          </Link>
          <div className="d-flex flex-column align-items-start flex-grow-1 text-start">
            <CampaignInfo item={item} />
          </div>
        </div>
      </td>

      <td className="text-start" style={{ minWidth: 160 }}>
        {item.winnerTicket ? (
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center text-primary bg-light border rounded p-3"
              title="Ticket vencedor."
            >
              <FaTicket />
            </div>
            <div>
              <div className="fw-bold text-dark" style={{ fontSize: "1.35rem" }}>
                {item.winnerTicket.ticket}
              </div>
              <small className="text-muted">
                Ordem #{masks.formatMilhar(item.winnerTicket.order)}
              </small>
            </div>
          </div>
        ) : (
          <span className="text-muted">Não informado</span>
        )}
      </td>

      <td className="text-start" style={{ minWidth: 360 }}>
        <RewardThumbStack item={item} />
        <RewardNames item={item} />
      </td>

      <td className="text-end text-nowrap" style={{ minWidth: 120 }}>
        <span className="fw-semibold d-block">
          {dayjs(item.lastUpdateAt).format("DD/MM/YYYY")}
        </span>
        <small className="text-muted">
          {dayjs(item.lastUpdateAt).format("HH:mm")}
        </small>
      </td>

      <td className="text-end text-nowrap" style={{ minWidth: 120 }}>
        <div className="d-flex justify-content-end gap-2">
          <Link
            to={getCampaignUrl(item)}
            className="btn btn-sm btn-outline-success d-inline-flex align-items-center justify-content-center p-2"
            title="Abrir campanha premiada."
          >
            <FaArrowUpRightFromSquare className="fs-6" />
          </Link>
          <Link
            to={getRewardInfoUrl(item)}
            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center p-2"
            title="Ver informações completas da premiação."
          >
            <FaEye className="fs-6" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

export function RewardCampaignMobileCard({ item }: IProps) {
  return (
    <div
      className="p-3 bg-white"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <div className="d-flex align-items-start justify-content-start gap-3 mb-3 text-start">
        <Link to={getCampaignUrl(item)} title="Abrir campanha premiada.">
          <RewardThumb
            src={item.raffle.thumb}
            alt={item.raffle.name}
            size={52}
          />
        </Link>
        <div className="d-flex flex-column align-items-start flex-grow-1 text-start">
          <CampaignInfo item={item} />
        </div>
      </div>

      <div className="d-flex justify-content-between gap-3 border-top pt-2 mb-3">
        <small className="text-muted">
          Ticket vencedor
          <span className="fw-bold text-dark d-block">
            {item.winnerTicket
              ? `${item.winnerTicket.ticket} | Ordem #${item.winnerTicket.order}`
              : "Não informado"}
          </span>
        </small>
        <small className="text-muted text-end">
          Atualização
          <span className="fw-bold text-dark d-block">
            {dayjs(item.lastUpdateAt).format("DD/MM/YYYY")}
          </span>
          <span className="text-muted d-block">
            {dayjs(item.lastUpdateAt).format("HH:mm")}
          </span>
        </small>
      </div>

      <div className="border-top pt-2">
        <small className="text-muted d-block mb-2">Prêmios</small>
        <RewardThumbStack item={item} />
        <RewardNames item={item} />
      </div>

      <div className="d-flex justify-content-end gap-2 border-top mt-3 pt-3">
        <Link
          to={getCampaignUrl(item)}
          className="btn btn-sm btn-outline-success d-inline-flex align-items-center justify-content-center p-2"
          title="Abrir campanha premiada."
        >
          <FaArrowUpRightFromSquare className="fs-6" />
        </Link>
        <Link
          to={getRewardInfoUrl(item)}
          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center p-2"
          title="Ver informações completas da premiação."
        >
          <FaEye className="fs-6" />
        </Link>
      </div>
    </div>
  );
}
